import { useRef, useState, useEffect } from 'react'
import { NavigationSettingsPanel } from './NavigationSettingsPanel'
import './NavigationSettingsBalloon.css'

interface NavigationSettingsBalloonProps {
  onClose: () => void
}

export function NavigationSettingsBalloon({
  onClose,
}: NavigationSettingsBalloonProps) {
  const balloonRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragStateRef = useRef({ startX: 0, startY: 0, lastX: 0, lastY: 0, offsetX: 0, offsetY: 0 })


  // Handle drag start on header
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!balloonRef.current) return
    
    // Get current transform offset if any
    const transform = balloonRef.current.style.transform
    let currentOffsetX = 0
    let currentOffsetY = 0
    
    if (transform && transform.includes('translate')) {
      // Try calc() pattern first: translate(calc(-50% + 100px), calc(-50% + 50px))
      let match = transform.match(/translate\(calc\([^)]+\s*\+\s*([-\d.]+)px\),\s*calc\([^)]+\s*\+\s*([-\d.]+)px\)\)/)
      if (match) {
        currentOffsetX = parseFloat(match[1])
        currentOffsetY = parseFloat(match[2])
      } else {
        // Fallback to simple translate: translate(100px, 50px)
        match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/)
        if (match) {
          currentOffsetX = parseFloat(match[1])
          currentOffsetY = parseFloat(match[2])
        }
      }
    }
    
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      offsetX: currentOffsetX,
      offsetY: currentOffsetY
    }
    setIsDragging(true)
  }

  // Handle drag movement
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!balloonRef.current) return

      // Calculate delta from last position
      const deltaX = e.clientX - dragStateRef.current.lastX
      const deltaY = e.clientY - dragStateRef.current.lastY

      // Update offset and last position
      dragStateRef.current.offsetX += deltaX
      dragStateRef.current.offsetY += deltaY
      dragStateRef.current.lastX = e.clientX
      dragStateRef.current.lastY = e.clientY

      // Apply position with transform: centering + offset
      balloonRef.current.style.transform = `translate(calc(-50% + ${dragStateRef.current.offsetX}px), calc(-50% + ${dragStateRef.current.offsetY}px))`
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  return (
    <div
      ref={balloonRef}
      className="navigation-settings-balloon"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Close button */}
      <button
        className="balloon-close"
        onClick={onClose}
        title="Close"
        aria-label="Close"
      >
        ✕
      </button>

      {/* Content */}
      <div className="balloon-content">
        <div
          ref={headerRef}
          className="balloon-header"
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          ⚙️ Navigation Settings
        </div>

        <div className="balloon-body">
          {/* NavigationSettingsPanel inside the balloon */}
          <NavigationSettingsPanel compact={false} />
        </div>
      </div>
    </div>
  )
}
